import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { axiosInstance } from "@/api";
import { baseURL } from "@/api/utils/base-url";

export function useServiceSearchQuery(query: string, options?: Options) {
  const debouncedQuery = useDebounce(query, options?.debounceTime ?? 300);
  const shouldFetch = debouncedQuery.length >= (options?.minQueryLength ?? 1);

  const queryResult = useQuery<ServiceSearchResult[]>({
    queryKey: ["service-search", debouncedQuery],
    queryFn: async () => {
      const response = await axiosInstance.get<ServiceSearchResponse>(
        "/search/services",
        {
          baseURL: baseURL.v2,
          params: {
            keyword: debouncedQuery,
          },
          meta: {
            disableToasts: true,
          },
        }
      );

      return Object.values(response.data.data).map(mapServiceSearchResult);
    },
    enabled: shouldFetch,
    placeholderData: (previousData) => previousData, // Keep previous results while loading new ones
  });

  return {
    services: queryResult.data ?? [],
    isLoading: queryResult.isLoading,
    isFetching: queryResult.isFetching,
    isError: queryResult.isError,
    query: debouncedQuery,
  };
}

interface Options {
  minQueryLength?: number;
  debounceTime?: number;
}

export type ServiceSearchResult = {
  id: string;
  uri: string;
  service_name: string;
};

interface ServiceSearchResponseItem {
  id: string | number;
  name: string;
  service_name?: string;
}

interface ServiceSearchResponse {
  data: Record<string, ServiceSearchResponseItem>;
}

function mapServiceSearchResult(
  service: ServiceSearchResponseItem
): ServiceSearchResult {
  return {
    id: String(service.id),
    uri: `/${service.name}`,
    service_name: service.service_name ?? service.name,
  };
}
